import type { AreaKey, ReportSummary, ResultBand } from '@/api/types';

export type Change = 'better' | 'same' | 'more_attention';

export interface AreaChange {
  reportId: string;
  areaKey: AreaKey;
  area: string;
  band: Exclude<ResultBand, 'na'>;
  previousBand: Exclude<ResultBand, 'na'>;
  previousDate: string;
  change: Change;
}

const RANK: Record<Exclude<ResultBand, 'na'>, number> = { ok: 0, watch: 1, discuss: 2 };
const scored = (r: ReportSummary): r is ReportSummary & { band: Exclude<ResultBand, 'na'>; assessmentKey: AreaKey } =>
  !!r.band && r.band !== 'na' && !!r.assessmentKey;

/**
 * For each area of one home assessment, how its band compares with the same child's previous home assessment
 * of that area. Words only — parents never see scores. Areas with no earlier result are left out.
 */
export const areaChanges = (reports: ReportSummary[], assessmentId: string): AreaChange[] => {
  const current = reports.filter((r) => r.assessmentId === assessmentId && scored(r));
  return current.flatMap((r) => {
    if (!scored(r)) return [];
    const previous = reports
      .filter((p) => p.childId === r.childId && p.assessmentKey === r.assessmentKey && p.assessmentId !== assessmentId && p.createdAt < r.createdAt && scored(p))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    if (!previous || !scored(previous)) return [];
    const diff = RANK[r.band] - RANK[previous.band];
    return [
      {
        reportId: r.reportId,
        areaKey: r.assessmentKey,
        area: r.area ?? r.assessmentKey,
        band: r.band,
        previousBand: previous.band,
        previousDate: previous.createdAt,
        change: diff < 0 ? 'better' : diff > 0 ? 'more_attention' : 'same',
      },
    ];
  });
};
